/** Bounds-checked little-endian reader/writer and a strict UTF-8 codec (no platform TextEncoder needed). */

export class ProtocolError extends Error {
  constructor(message: string) { super(message); this.name = 'ProtocolError'; }
}

export class Writer {
  private buf: Uint8Array;
  private view: DataView;
  private pos = 0;
  constructor(capacity = 32) { this.buf = new Uint8Array(capacity); this.view = new DataView(this.buf.buffer); }

  private room(n: number) {
    if (this.pos + n <= this.buf.length) return;
    let cap = this.buf.length * 2;
    while (cap < this.pos + n) cap *= 2;
    const next = new Uint8Array(cap); next.set(this.buf.subarray(0, this.pos));
    this.buf = next; this.view = new DataView(next.buffer);
  }
  u8(v: number) { this.room(1); this.view.setUint8(this.pos, v); this.pos += 1; return this; }
  u16(v: number) { this.room(2); this.view.setUint16(this.pos, v, true); this.pos += 2; return this; }
  i16(v: number) { this.room(2); this.view.setInt16(this.pos, v, true); this.pos += 2; return this; }
  u32(v: number) { this.room(4); this.view.setUint32(this.pos, v, true); this.pos += 4; return this; }
  f64(v: number) { this.room(8); this.view.setFloat64(this.pos, v, true); this.pos += 8; return this; }
  bytes(b: Uint8Array) { this.room(b.length); this.buf.set(b, this.pos); this.pos += b.length; return this; }
  /** u8 length prefix, at most 255 bytes of UTF-8. */
  str(s: string) {
    const b = utf8Encode(s);
    if (b.length > 255) throw new ProtocolError('string longer than 255 bytes');
    return this.u8(b.length).bytes(b);
  }
  finish(): Uint8Array { return this.buf.slice(0, this.pos); }
}

export class Reader {
  private readonly buf: Uint8Array;
  private readonly view: DataView;
  private pos = 0;
  constructor(buf: Uint8Array) { this.buf = buf; this.view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength); }

  private need(n: number) { if (this.pos + n > this.buf.length) throw new ProtocolError('message truncated'); }
  u8() { this.need(1); const v = this.view.getUint8(this.pos); this.pos += 1; return v; }
  u16() { this.need(2); const v = this.view.getUint16(this.pos, true); this.pos += 2; return v; }
  i16() { this.need(2); const v = this.view.getInt16(this.pos, true); this.pos += 2; return v; }
  u32() { this.need(4); const v = this.view.getUint32(this.pos, true); this.pos += 4; return v; }
  f64() {
    this.need(8); const v = this.view.getFloat64(this.pos, true); this.pos += 8;
    if (!Number.isFinite(v)) throw new ProtocolError('non-finite number');
    return v;
  }
  bytes(n: number) { this.need(n); const b = this.buf.slice(this.pos, this.pos + n); this.pos += n; return b; }
  str() { return utf8Decode(this.bytes(this.u8())); }
  /** A field restricted to a table's indices. */
  index<T>(table: readonly T[], what: string): T {
    const i = this.u8();
    if (i >= table.length) throw new ProtocolError(`unknown ${what} ${i}`);
    return table[i];
  }
  end() { if (this.pos !== this.buf.length) throw new ProtocolError('trailing bytes'); }
}

export function utf8Encode(s: string): Uint8Array {
  const out: number[] = [];
  for (let i = 0; i < s.length; i++) {
    let c = s.charCodeAt(i);
    if (c >= 0xd800 && c < 0xdc00 && i + 1 < s.length) {
      const d = s.charCodeAt(i + 1);
      if (d >= 0xdc00 && d < 0xe000) { c = 0x10000 + ((c - 0xd800) << 10) + (d - 0xdc00); i++; }
    }
    if (c >= 0xd800 && c < 0xe000) c = 0xfffd; // lone surrogate
    if (c < 0x80) out.push(c);
    else if (c < 0x800) out.push(0xc0 | (c >> 6), 0x80 | (c & 63));
    else if (c < 0x10000) out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
    else out.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 63), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
  }
  return Uint8Array.from(out);
}

/** Strict decoding: overlong forms, surrogates and truncated sequences are errors, not U+FFFD. */
export function utf8Decode(b: Uint8Array): string {
  let s = '';
  for (let i = 0; i < b.length;) {
    const a = b[i];
    let c: number, n: number, min: number;
    if (a < 0x80) { c = a; n = 0; min = 0; }
    else if ((a & 0xe0) === 0xc0) { c = a & 31; n = 1; min = 0x80; }
    else if ((a & 0xf0) === 0xe0) { c = a & 15; n = 2; min = 0x800; }
    else if ((a & 0xf8) === 0xf0) { c = a & 7; n = 3; min = 0x10000; }
    else throw new ProtocolError('invalid UTF-8');
    if (n && i + n >= b.length) throw new ProtocolError('truncated UTF-8');
    for (let k = 1; k <= n; k++) {
      const x = b[i + k];
      if ((x & 0xc0) !== 0x80) throw new ProtocolError('invalid UTF-8');
      c = (c << 6) | (x & 63);
    }
    if (c < min || c > 0x10ffff || (c >= 0xd800 && c < 0xe000)) throw new ProtocolError('invalid UTF-8');
    s += String.fromCodePoint(c);
    i += n + 1;
  }
  return s;
}
